import { useEffect, useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isSameDay,
} from "date-fns";
import { fr } from "date-fns/locale";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Loader2, CalendarCheck2 } from "lucide-react";

const WEEKDAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

export default function AvailabilityPage() {
  const [cursor, setCursor] = useState(new Date());
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [range, setRange] = useState({ start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/availability/me", {
        params: { start: format(gridStart, "yyyy-MM-dd"), end: format(gridEnd, "yyyy-MM-dd") },
      });
      setAbsences(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  const days = useMemo(() => {
    const arr = [];
    let d = gridStart;
    while (d <= gridEnd) { arr.push(d); d = addDays(d, 1); }
    return arr;
  }, [gridStart, gridEnd]);

  const byDate = useMemo(() => {
    const map = {};
    for (const a of absences) map[a.date] = a;
    return map;
  }, [absences]);

  const seriesCount = useMemo(() => {
    const c = {};
    for (const a of absences) c[a.series_id] = (c[a.series_id] || 0) + 1;
    return c;
  }, [absences]);

  const openAdd = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    setRange({ start_date: today, end_date: today });
    setAddOpen(true);
  };

  const addAbsence = async () => {
    if (!range.start_date || !range.end_date) return toast.error("Sélectionnez les dates.");
    if (range.start_date > range.end_date) return toast.error("La date de fin doit suivre la date de début.");
    setSaving(true);
    try {
      await api.post("/availability/absences", range);
      toast.success("Absence ajoutée.");
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onCancelClick = (absence) => {
    if (seriesCount[absence.series_id] > 1) {
      setCancelTarget(absence);
    } else {
      deleteDay(absence);
    }
  };

  const deleteDay = async (absence) => {
    try {
      await api.delete(`/availability/day/${absence.id}`);
      toast.success("Absence annulée pour cette journée.");
      setCancelTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteSeries = async (absence) => {
    try {
      await api.delete(`/availability/series/${absence.series_id}`);
      toast.success("Série d'absences annulée.");
      setCancelTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <CalendarCheck2 className="h-7 w-7 text-primary" /> Mes disponibilités
          </h1>
          <p className="text-muted-foreground mt-1">
            Vous êtes disponible (<span className="text-emerald-600 dark:text-emerald-400 font-medium">vert</span>) par défaut. Ajoutez vos absences (<span className="text-red-600 dark:text-red-400 font-medium">rouge</span>).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-absence-btn" onClick={openAdd} className="active:scale-95 transition-transform">
            <Plus className="h-4 w-4 mr-2" /> Ajouter une absence
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-4">
        <Button data-testid="prev-month-btn" variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-[170px] text-center font-heading text-lg font-semibold capitalize">
          {format(cursor, "MMMM yyyy", { locale: fr })}
        </div>
        <Button data-testid="next-month-btn" variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">{d}</div>
          ))}
        </div>
        {loading ? (
          <div className="grid place-items-center py-32 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const absence = byDate[key];
              const inMonth = isSameMonth(day, cursor);
              const today = isSameDay(day, new Date());
              const absent = !!absence;
              return (
                <div
                  key={key}
                  data-testid={`avail-cell-${key}`}
                  className={`relative min-h-[80px] border-b border-r border-border p-1.5 transition-colors ${
                    !inMonth ? "bg-muted/20" : absent
                      ? "bg-red-500/15"
                      : "bg-emerald-500/10"
                  }`}
                >
                  <div className={`text-xs font-medium flex items-center justify-center h-6 w-6 rounded-full ${today ? "bg-primary text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground/40"}`}>
                    {format(day, "d")}
                  </div>
                  {inMonth && absent && (
                    <button
                      data-testid={`cancel-absence-${key}`}
                      onClick={() => onCancelClick(absence)}
                      title="Annuler cette absence"
                      className="absolute top-1 right-1 grid place-items-center h-5 w-5 rounded-full bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/40 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  {inMonth && (
                    <span className={`absolute bottom-1 left-1.5 text-[10px] font-medium ${absent ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {absent ? "Absent" : "Dispo"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-absence-modal">
          <DialogHeader><DialogTitle className="font-heading">Ajouter une absence</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Date de début</Label>
              <Input data-testid="absence-start-input" type="date" value={range.start_date} onChange={(e) => setRange({ ...range, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Date de fin</Label>
              <Input data-testid="absence-end-input" type="date" value={range.end_date} onChange={(e) => setRange({ ...range, end_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button data-testid="absence-save-btn" onClick={addAbsence} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-series-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler cette absence</AlertDialogTitle>
            <AlertDialogDescription>
              Cette absence fait partie d'une série de {cancelTarget && seriesCount[cancelTarget.series_id]} jours. Souhaitez-vous annuler seulement cette journée ou toute la série ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Fermer</AlertDialogCancel>
            <Button data-testid="cancel-day-btn" variant="secondary" onClick={() => deleteDay(cancelTarget)}>Cette journée</Button>
            <AlertDialogAction data-testid="cancel-series-btn" onClick={() => deleteSeries(cancelTarget)}>Toute la série</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
